import { useState, useEffect } from 'react';
import {
  Shield,
  AlertTriangle,
  Bug,
  Key,
  GitBranch,
  RefreshCw,
  ExternalLink,
  Package,
  Calendar,
  Code,
  X,
  CheckCircle,
  Clock,
  ArrowUpCircle,
  Download,
  Settings,
  Trash2,
} from 'lucide-react';
import type {
  SecuritySummary,
  SecurityRepository,
  Vulnerability,
  SecretAlert,
  CodeAlert,
} from '../types/security';
import './Security.css';

type TabType = 'vulnerabilities' | 'secrets' | 'code';

// Default empty summary
const defaultSummary: SecuritySummary = {
  totalRepos: 0,
  totalVulnerabilities: 0,
  criticalCount: 0,
  highCount: 0,
  mediumCount: 0,
  lowCount: 0,
  openSecrets: 0,
  codeAlerts: 0,
  repoBreakdown: [],
};

export default function Security() {
  const [activeTab, setActiveTab] = useState<TabType>('vulnerabilities');
  const [summary, setSummary] = useState<SecuritySummary>(defaultSummary);
  const [vulnerabilities, setVulnerabilities] = useState<Vulnerability[]>([]);
  const [secrets, setSecrets] = useState<SecretAlert[]>([]);
  const [codeAlerts, setCodeAlerts] = useState<CodeAlert[]>([]);
  const [repos, setRepos] = useState<SecurityRepository[]>([]);
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVuln, setSelectedVuln] = useState<Vulnerability | null>(null);
  const [selectedCodeAlert, setSelectedCodeAlert] = useState<CodeAlert | null>(null);
  const [showReposDrawer, setShowReposDrawer] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [githubOrgs, setGithubOrgs] = useState<{ user: string | null; organizations: string[] }>({ user: null, organizations: [] });
  const [selectedOwner, setSelectedOwner] = useState<string>('');
  const [customOwner, setCustomOwner] = useState<string>('');

  useEffect(() => {
    const fetchSecurityData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [summaryRes, vulnRes, secretRes, codeRes, repoRes] = await Promise.all([
          fetch('/api/security/summary'),
          fetch('/api/security/vulnerabilities'),
          fetch('/api/security/secrets'),
          fetch('/api/security/code-alerts'),
          fetch('/api/security/repos'),
        ]);

        if (summaryRes.ok) {
          const data = await summaryRes.json();
          setSummary(data);
        }

        if (vulnRes.ok) {
          const data = await vulnRes.json();
          if (data?.vulnerabilities) setVulnerabilities(data.vulnerabilities);
        }

        if (secretRes.ok) {
          const data = await secretRes.json();
          if (data?.secrets) setSecrets(data.secrets);
        }

        if (codeRes.ok) {
          const data = await codeRes.json();
          if (data?.alerts) setCodeAlerts(data.alerts);
        }

        if (repoRes.ok) {
          const data = await repoRes.json();
          if (data?.repos) setRepos(data.repos);
        }
      } catch (err) {
        console.error('Failed to fetch security data:', err);
        setError('Failed to load security data');
      } finally {
        setLoading(false);
      }
    };

    fetchSecurityData();
  }, []);

  const handleScanAll = async () => {
    setScanning(true);
    try {
      const res = await fetch('/api/security/scan', { method: 'POST' });
      if (res.ok) {
        // Refresh data after scan
        const [summaryData, vulnData] = await Promise.all([
          fetch('/api/security/summary').then(r => r.ok ? r.json() : null),
          fetch('/api/security/vulnerabilities').then(r => r.ok ? r.json() : null),
        ]);
        if (summaryData) setSummary(summaryData);
        if (vulnData?.vulnerabilities) setVulnerabilities(vulnData.vulnerabilities);
      }
    } catch {
      // Handle error
    } finally {
      setScanning(false);
    }
  };

  const handleScanRepo = async (owner: string, repo: string) => {
    try {
      await fetch(`/api/security/scan/${owner}/${repo}`, { method: 'POST' });
      // Refresh repos list
      const repoData = await fetch('/api/security/repos').then(r => r.ok ? r.json() : null);
      if (repoData?.repos) setRepos(repoData.repos);
    } catch {
      // Handle error
    }
  };

  const handleOpenSyncModal = async () => {
    setShowSyncModal(true);
    // Fetch available orgs/user
    try {
      const res = await fetch('/api/security/github/orgs');
      if (res.ok) {
        const data = await res.json();
        setGithubOrgs({ user: data.user, organizations: data.organizations || [] });
        // Set default selection
        if (data.organizations?.length > 0) {
          setSelectedOwner(data.organizations[0]);
        } else if (data.user) {
          setSelectedOwner(data.user);
        }
      }
    } catch {
      // Handle error silently
    }
  };

  const handleSyncRepos = async () => {
    const owner = selectedOwner === 'custom' ? customOwner : selectedOwner;
    if (!owner) return;

    setSyncing(true);
    try {
      const res = await fetch('/api/security/github/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner }),
      });

      if (res.ok) {
        const data = await res.json();
        alert(`Successfully synced ${data.added} new repositories from ${owner}.\n${data.existing} repositories already existed.`);
        // Refresh repos list
        const repoData = await fetch('/api/security/repos').then(r => r.ok ? r.json() : null);
        if (repoData?.repos) setRepos(repoData.repos);
        // Update summary
        const summaryData = await fetch('/api/security/summary').then(r => r.ok ? r.json() : null);
        if (summaryData) setSummary(summaryData);
        setShowSyncModal(false);
      } else {
        const errData = await res.json();
        alert(`Failed to sync: ${errData.error || 'Unknown error'}`);
      }
    } catch {
      alert('Failed to sync repositories. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const handleRemoveRepo = async (owner: string, repo: string) => {
    if (!confirm(`Remove ${owner}/${repo} from monitoring?`)) return;

    try {
      const res = await fetch(`/api/security/repos/${owner}/${repo}`, { method: 'DELETE' });
      if (res.ok) {
        setRepos(prev => prev.filter(r => !(r.owner === owner && r.repo === repo)));
        // Update summary
        const summaryData = await fetch('/api/security/summary').then(r => r.ok ? r.json() : null);
        if (summaryData) setSummary(summaryData);
      }
    } catch {
      alert('Failed to remove repository');
    }
  };

  const filteredVulnerabilities = vulnerabilities.filter(v =>
    severityFilter === 'all' || v.severity === severityFilter
  );

  const formatTimeAgo = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="security-loading">Loading security data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Security</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#f38ba8' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <h1>Security</h1>
          <p>GitHub vulnerability scanning and security monitoring</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => setShowReposDrawer(true)}>
            <Settings size={16} />
            Manage Repos ({summary.totalRepos})
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="security-stats">
        <div className="security-stat">
          <div className="security-stat-icon critical">
            <AlertTriangle size={24} />
          </div>
          <div className="security-stat-content">
            <div className="security-stat-value">{summary.criticalCount}</div>
            <div className="security-stat-label">Critical</div>
          </div>
        </div>
        <div className="security-stat">
          <div className="security-stat-icon high">
            <Bug size={24} />
          </div>
          <div className="security-stat-content">
            <div className="security-stat-value">{summary.highCount}</div>
            <div className="security-stat-label">High</div>
          </div>
        </div>
        <div className="security-stat">
          <div className="security-stat-icon medium">
            <Shield size={24} />
          </div>
          <div className="security-stat-content">
            <div className="security-stat-value">{summary.totalVulnerabilities}</div>
            <div className="security-stat-label">Total Vulnerabilities</div>
          </div>
        </div>
        <div className="security-stat">
          <div className="security-stat-icon repos">
            <GitBranch size={24} />
          </div>
          <div className="security-stat-content">
            <div className="security-stat-value">{summary.totalRepos}</div>
            <div className="security-stat-label">Repositories</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="security-tabs">
        <button
          className={`security-tab ${activeTab === 'vulnerabilities' ? 'active' : ''}`}
          onClick={() => setActiveTab('vulnerabilities')}
        >
          <Bug size={16} />
          Vulnerabilities
          <span className="count">{summary.totalVulnerabilities}</span>
        </button>
        <button
          className={`security-tab ${activeTab === 'secrets' ? 'active' : ''}`}
          onClick={() => setActiveTab('secrets')}
        >
          <Key size={16} />
          Secrets
          <span className="count">{summary.openSecrets}</span>
        </button>
        <button
          className={`security-tab ${activeTab === 'code' ? 'active' : ''}`}
          onClick={() => setActiveTab('code')}
        >
          <Code size={16} />
          Code Alerts
          <span className="count">{summary.codeAlerts}</span>
        </button>
      </div>

      {/* Actions Bar */}
      <div className="security-actions">
        <div className="security-filters">
          {activeTab === 'vulnerabilities' && (
            <select
              className="filter-select"
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          )}
        </div>
        <button
          className="scan-all-btn"
          onClick={handleScanAll}
          disabled={scanning}
        >
          <RefreshCw size={16} className={scanning ? 'spinning' : ''} />
          {scanning ? 'Scanning...' : 'Scan All Repos'}
        </button>
      </div>

      {/* Content */}
      {activeTab === 'vulnerabilities' && (
        <div className="vulnerability-list">
          {filteredVulnerabilities.length === 0 ? (
            <div className="security-empty">
              <div className="security-empty-icon">
                <Shield size={48} />
              </div>
              <h3>No vulnerabilities found</h3>
              <p>All monitored repositories are clean.</p>
            </div>
          ) : (
            filteredVulnerabilities.map((vuln) => (
              <div
                key={vuln.id}
                className="vulnerability-item clickable"
                onClick={() => setSelectedVuln(vuln)}
              >
                <div className="vulnerability-header">
                  <div>
                    <div className="vulnerability-title">{vuln.summary}</div>
                    <div className="vulnerability-meta">
                      <span className="vulnerability-package">
                        <Package size={12} />
                        {vuln.package_name}
                      </span>
                      <span>
                        <GitBranch size={12} />
                        {vuln.repo?.owner}/{vuln.repo?.repo}
                      </span>
                      {vuln.cvss_score && (
                        <span>CVSS: {vuln.cvss_score}</span>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span className={`severity-badge ${vuln.severity}`}>
                      {vuln.severity}
                    </span>
                    <span className="vulnerability-id">
                      {vuln.cve_id || vuln.ghsa_id}
                    </span>
                  </div>
                </div>
                <div className="vulnerability-description">
                  {vuln.description?.substring(0, 200)}
                  {vuln.description?.length > 200 ? '...' : ''}
                </div>
                {vuln.patched_version && (
                  <div className="vulnerability-meta" style={{ marginTop: '0.5rem' }}>
                    <span style={{ color: '#a6e3a1' }}>
                      Fix available: upgrade to {vuln.patched_version}
                    </span>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'secrets' && (
        <div className="vulnerability-list">
          {secrets.length === 0 ? (
            <div className="security-empty">
              <div className="security-empty-icon">
                <Key size={48} />
              </div>
              <h3>No exposed secrets</h3>
              <p>No secret scanning alerts found.</p>
            </div>
          ) : (
            secrets.map((secret) => (
              <div key={secret.id} className="secret-item">
                <div className="secret-icon">
                  <Key size={20} />
                </div>
                <div className="secret-content">
                  <div className="secret-type">{secret.secret_type_display}</div>
                  <div className="secret-repo">
                    {secret.repo?.owner}/{secret.repo?.repo}
                  </div>
                </div>
                <a
                  href={secret.html_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="secret-link"
                >
                  <ExternalLink size={14} />
                  View on GitHub
                </a>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'code' && (
        <div className="vulnerability-list">
          {codeAlerts.length === 0 ? (
            <div className="security-empty">
              <div className="security-empty-icon">
                <Code size={48} />
              </div>
              <h3>No code scanning alerts</h3>
              <p>CodeQL analysis found no issues.</p>
            </div>
          ) : (
            codeAlerts.map((alert) => (
              <div
                key={alert.id}
                className="vulnerability-item clickable"
                onClick={() => setSelectedCodeAlert(alert)}
              >
                <div className="vulnerability-header">
                  <div>
                    <div className="vulnerability-title">{alert.rule_description}</div>
                    <div className="vulnerability-meta">
                      <span>
                        <Code size={12} />
                        {alert.file_path}:{alert.start_line}
                      </span>
                      <span>
                        <GitBranch size={12} />
                        {alert.repo?.owner}/{alert.repo?.repo}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <span className={`severity-badge ${alert.severity}`}>
                      {alert.severity}
                    </span>
                    <a
                      href={alert.html_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="secret-link"
                    >
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Repos Management Drawer */}
      {showReposDrawer && (
        <div className="drawer-overlay" onClick={() => setShowReposDrawer(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h2>Manage Repositories</h2>
              <button className="close-btn" onClick={() => setShowReposDrawer(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="drawer-actions">
              <button className="btn btn-primary" onClick={handleOpenSyncModal}>
                <Download size={16} />
                Sync from GitHub
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleScanAll}
                disabled={scanning || repos.length === 0}
              >
                <RefreshCw size={16} className={scanning ? 'spin' : ''} />
                {scanning ? 'Scanning...' : 'Scan All'}
              </button>
            </div>
            <div className="drawer-body">
              {repos.length === 0 ? (
                <div className="drawer-empty">
                  <GitBranch size={32} />
                  <p>No repositories configured</p>
                  <p className="hint">Click "Sync from GitHub" to import repos</p>
                </div>
              ) : (
                <div className="repo-list-compact">
                  {repos.map((repo) => (
                    <div key={repo.id} className="repo-item">
                      <div className="repo-item-info">
                        <GitBranch size={14} />
                        <span className="repo-item-name">{repo.owner}/{repo.repo}</span>
                      </div>
                      <div className="repo-item-actions">
                        <span className="repo-item-scan">
                          {formatTimeAgo(repo.lastScanAt)}
                        </span>
                        <button
                          className="btn-icon"
                          onClick={() => handleScanRepo(repo.owner, repo.repo)}
                          title="Scan now"
                        >
                          <RefreshCw size={14} />
                        </button>
                        <button
                          className="btn-icon danger"
                          onClick={() => handleRemoveRepo(repo.owner, repo.repo)}
                          title="Remove"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Vulnerability Detail Modal */}
      {selectedVuln && (
        <div className="modal-overlay" onClick={() => setSelectedVuln(null)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-row">
                <h2>{selectedVuln.summary}</h2>
                <span className={`severity-badge ${selectedVuln.severity}`}>
                  {selectedVuln.severity}
                </span>
              </div>
              <button className="close-btn" onClick={() => setSelectedVuln(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="vuln-ids">
                {selectedVuln.cve_id && (
                  <span className="vuln-id-badge">{selectedVuln.cve_id}</span>
                )}
                {selectedVuln.ghsa_id && (
                  <span className="vuln-id-badge">{selectedVuln.ghsa_id}</span>
                )}
                {selectedVuln.cvss_score && (
                  <span className="vuln-cvss">CVSS: {selectedVuln.cvss_score}</span>
                )}
              </div>

              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">Package</span>
                  <span><Package size={14} style={{ marginRight: '6px' }} />{selectedVuln.package_name}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Vulnerable Versions</span>
                  <span>{selectedVuln.vulnerable_versions || 'Not specified'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Repository</span>
                  <span><GitBranch size={14} style={{ marginRight: '6px' }} />{selectedVuln.repo?.owner}/{selectedVuln.repo?.repo}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">State</span>
                  <span>{selectedVuln.state || 'Open'}</span>
                </div>
              </div>

              {selectedVuln.description && (
                <div className="detail-section">
                  <h3>Description</h3>
                  <p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    {selectedVuln.description}
                  </p>
                </div>
              )}

              {selectedVuln.patched_version && (
                <div className="vuln-fix-section">
                  <ArrowUpCircle size={20} className="fix-icon" />
                  <div>
                    <strong>Fix Available</strong>
                    <p>Upgrade to version {selectedVuln.patched_version}</p>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                {selectedVuln.html_url && (
                  <a
                    href={selectedVuln.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    <ExternalLink size={16} />
                    View on GitHub
                  </a>
                )}
                <button className="btn btn-secondary">
                  <CheckCircle size={16} />
                  Mark as Reviewed
                </button>
                <button className="btn btn-secondary">
                  <Clock size={16} />
                  Defer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Code Alert Detail Modal */}
      {selectedCodeAlert && (
        <div className="modal-overlay" onClick={() => setSelectedCodeAlert(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-row">
                <h2>Code Alert</h2>
                <span className={`severity-badge ${selectedCodeAlert.severity}`}>
                  {selectedCodeAlert.severity}
                </span>
              </div>
              <button className="close-btn" onClick={() => setSelectedCodeAlert(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <h3 style={{ marginBottom: '12px' }}>{selectedCodeAlert.rule_description}</h3>

              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">File</span>
                  <span><Code size={14} style={{ marginRight: '6px' }} />{selectedCodeAlert.file_path}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Line</span>
                  <span>{selectedCodeAlert.start_line}{selectedCodeAlert.end_line && selectedCodeAlert.end_line !== selectedCodeAlert.start_line ? `-${selectedCodeAlert.end_line}` : ''}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Repository</span>
                  <span><GitBranch size={14} style={{ marginRight: '6px' }} />{selectedCodeAlert.repo?.owner}/{selectedCodeAlert.repo?.repo}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Rule ID</span>
                  <span>{selectedCodeAlert.rule_id}</span>
                </div>
              </div>

              <div className="modal-actions">
                {selectedCodeAlert.html_url && (
                  <a
                    href={selectedCodeAlert.html_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    <ExternalLink size={16} />
                    View on GitHub
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sync from GitHub Modal */}
      {showSyncModal && (
        <div className="modal-overlay" onClick={() => setShowSyncModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Sync Repositories from GitHub</h2>
              <button className="close-btn" onClick={() => setShowSyncModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                Import all repositories from a GitHub organization or user account for security monitoring.
              </p>

              <div className="form-group">
                <label>Select Organization or User</label>
                <select
                  value={selectedOwner}
                  onChange={(e) => setSelectedOwner(e.target.value)}
                  disabled={syncing}
                >
                  {githubOrgs.user && (
                    <option value={githubOrgs.user}>{githubOrgs.user} (Your Account)</option>
                  )}
                  {githubOrgs.organizations.map((org) => (
                    <option key={org} value={org}>{org}</option>
                  ))}
                  <option value="custom">Enter custom...</option>
                </select>
              </div>

              {selectedOwner === 'custom' && (
                <div className="form-group">
                  <label>Organization or Username</label>
                  <input
                    type="text"
                    value={customOwner}
                    onChange={(e) => setCustomOwner(e.target.value)}
                    placeholder="e.g., PickBitsAI"
                    disabled={syncing}
                  />
                </div>
              )}

              <div className="sync-info">
                <p>This will:</p>
                <ul>
                  <li>Fetch all non-archived repositories</li>
                  <li>Add them to security monitoring</li>
                  <li>Skip repositories already being monitored</li>
                </ul>
              </div>

              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowSyncModal(false)}
                  disabled={syncing}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSyncRepos}
                  disabled={syncing || (!selectedOwner || (selectedOwner === 'custom' && !customOwner))}
                >
                  <Download size={16} className={syncing ? 'spin' : ''} />
                  {syncing ? 'Syncing...' : 'Sync Repositories'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
